import UmbSpriteIcon from "#/_packages/umbrella/features/icons/components/umb-sprite-icon";
import { useCallback, useEffect, useState } from "react";
import { cn, tv } from "tailwind-variants";
import { m } from "#/paraglide/messages";

export type BackToTopVariant = "left" | "center" | "right";
export type BackToTopButtonVariant = "none" | "text";

export type BackToTopVisibility = {
	base?: boolean;
	sm?: boolean;
	md?: boolean;
	lg?: boolean;
	xl?: boolean;
};

export type BackToTopProps = {
	label?: string;
	variant?: BackToTopVariant;
	showIcon?: boolean;
	buttonVariant?: BackToTopButtonVariant;
	visibility?: string[];
	enableSmoothScroll?: boolean;
	scrollThreshold?: number;
	className?: string;
};

const backToTop = tv({
	base: "back-to-top",
	variants: {
		variant: {
			left: "left-4",
			center: "left-1/2 -translate-x-1/2",
			right: "right-4",
		},
	},
});

function toVisibilityClasses(visibility?: string[]) {
	if (!visibility) {
		return "block";
	}

	return [
		isVisibleAt(visibility, "xs", "block") ? "block" : "hidden",
		isVisibleAt(visibility, "sm", "sm:block") ? "sm:block" : "sm:hidden",
		isVisibleAt(visibility, "md", "md:block") ? "md:block" : "md:hidden",
		isVisibleAt(visibility, "lg", "lg:block") ? "lg:block" : "lg:hidden",
		isVisibleAt(visibility, "xl", "xl:block") ? "xl:block" : "xl:hidden",
	].join(" ");
}

function isVisibleAt(
	visibility: string[],
	breakpoint: string,
	bootstrapClass: string,
) {
	return visibility.includes(breakpoint) || visibility.includes(bootstrapClass);
}

function ArrowUpIcon() {
    return <UmbSpriteIcon icon="lucide-arrow-up" />;
}

export function BackToTop({
	label,
	variant = "right",
	showIcon = true,
	buttonVariant = "text",
	visibility,
	enableSmoothScroll = true,
	scrollThreshold = 300,
	className,
}: BackToTopProps) {
	const resolvedLabel = label ?? m.back_to_top();
	const [isVisible, setIsVisible] = useState(scrollThreshold <= 0);

	useEffect(() => {
		if (scrollThreshold <= 0) {
			setIsVisible(true);
			return;
		}

		const updateVisibility = () => {
			setIsVisible(window.scrollY >= scrollThreshold);
		};

		updateVisibility();
		window.addEventListener("scroll", updateVisibility, { passive: true });

		return () => {
			window.removeEventListener("scroll", updateVisibility);
		};
	}, [scrollThreshold]);

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			if (!enableSmoothScroll || typeof window === "undefined") {
				return;
			}

			event.preventDefault();
			window.scrollTo({ top: 0, behavior: "smooth" });
		},
		[enableSmoothScroll],
	);

	if (!isVisible) {
		return null;
	}

	return (
		<nav
			className={cn(
				backToTop({ variant }),
				toVisibilityClasses(visibility),
				className,
			)}
			aria-label={resolvedLabel}
		>
			<button
				type="button"
				className="back-to-top-link"
				onClick={(e) => {
					handleClick(e);
				}}
			>
				{showIcon ? <ArrowUpIcon /> : null}
				{buttonVariant === "none" ? (
					<span className="sr-only">{resolvedLabel}</span>
				) : (
					<span className="text-label">{resolvedLabel}</span>
				)}
			</button>
		</nav>
	);
}
